import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getScenarios, createScenario, updateScenario, deleteScenario,
  createTestCase, updateTestCase, replaceTestCaseFiles, deleteTestCase, runTestCase,
} from '../api/client'
import { useStore } from '../store'
import type { Scenario, TestCase } from '../types'
import { ScenarioForm, TestCaseForm, TestCaseRow } from './ScenarioComponents'

interface ScenarioCardProps {
  scenario: Scenario
  isAdmin: boolean
  onRefresh: () => void
}

function ScenarioCard({ scenario, isAdmin, onRefresh }: ScenarioCardProps) {
  const { t } = useTranslation()
  const { setCurrentSession, setLoadedTestCase, setActiveView, setError } = useStore()
  const [expanded, setExpanded] = useState(false)
  const [editingScenario, setEditingScenario] = useState(false)
  const [addingCase, setAddingCase] = useState(false)
  const [saving, setSaving] = useState(false)
  const [runningId, setRunningId] = useState<number | null>(null)

  const handleUpdateScenario = async (name: string, description: string) => {
    setSaving(true)
    try {
      await updateScenario(scenario.id, { name, description })
      onRefresh()
      setEditingScenario(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('scenario_panel.error_update'))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteScenario = async () => {
    if (!window.confirm(t('scenario_panel.confirm_delete_scenario', { name: scenario.name }))) return
    try {
      await deleteScenario(scenario.id)
      onRefresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('scenario_panel.error_delete'))
    }
  }

  const handleAddCase = async (name: string, description: string, files: File[]) => {
    setSaving(true)
    try {
      await createTestCase(scenario.id, name, description, files)
      onRefresh()
      setAddingCase(false)
      setExpanded(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('scenario_panel.error_create'))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteCase = async (tc: TestCase) => {
    if (!window.confirm(t('scenario_panel.confirm_delete_testcase', { name: tc.name }))) return
    try {
      await deleteTestCase(tc.id)
      onRefresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('scenario_panel.error_delete'))
    }
  }

  const handleUpdateCase = async (tc: TestCase, name: string, description: string, files: File[]) => {
    await updateTestCase(tc.id, { name, description })
    if (files.length > 0) {
      await replaceTestCaseFiles(tc.id, files)
    }
    onRefresh()
  }

  const handleRun = async (tc: TestCase) => {
    setRunningId(tc.id)
    try {
      const session = await runTestCase(tc.id)
      setCurrentSession(session)
      setLoadedTestCase({ scenarioName: scenario.name, testCaseName: tc.name })
      setActiveView('result')
    } catch (e) {
      setError(e instanceof Error ? e.message : t('scenario_panel.error_run'))
    } finally {
      setRunningId(null)
    }
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {editingScenario ? (
        <div className="p-4">
          <ScenarioForm
            initial={{ name: scenario.name, description: scenario.description ?? '' }}
            onSave={handleUpdateScenario}
            onCancel={() => setEditingScenario(false)}
            saving={saving}
          />
        </div>
      ) : (
        <div
          className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          onClick={() => setExpanded((v) => !v)}
        >
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-sm select-none">{expanded ? '▾' : '▸'}</span>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{scenario.name}</p>
              {scenario.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400">{scenario.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {scenario.cases.length} {scenario.cases.length !== 1 ? t('scenario_panel.test_cases_plural') : t('scenario_panel.test_case_singular')}
            </span>
            {isAdmin && (
              <>
                <button onClick={() => setEditingScenario(true)} className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                  {t('scenario_panel.edit_button')}
                </button>
                <button onClick={handleDeleteScenario} className="text-xs text-red-400 hover:text-red-600">
                  {t('scenario_panel.delete_button')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-3 flex flex-col gap-2">
          {scenario.cases.length === 0 && !addingCase && (
            <p className="text-xs text-gray-400 dark:text-gray-500 italic">{t('scenario_panel.no_test_cases')}</p>
          )}
          {scenario.cases.map((tc) => (
            <TestCaseRow
              key={tc.id}
              tc={tc}
              isAdmin={isAdmin}
              onRun={handleRun}
              onDelete={handleDeleteCase}
              onUpdate={handleUpdateCase}
              running={runningId === tc.id}
            />
          ))}
          {addingCase ? (
            <TestCaseForm
              filesRequired
              onSave={handleAddCase}
              onCancel={() => setAddingCase(false)}
              saving={saving}
            />
          ) : (
            isAdmin && (
              <button
                onClick={() => setAddingCase(true)}
                className="text-xs text-blue-600 hover:underline self-start mt-1"
              >
                {t('scenario_panel.add_test_case_button')}
              </button>
            )
          )}
        </div>
      )}
    </div>
  )
}

export function ScenarioPanel() {
  const { t } = useTranslation()
  const { setError, currentUser } = useStore()
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [loading, setLoading] = useState(true)
  const [addingScenario, setAddingScenario] = useState(false)
  const [saving, setSaving] = useState(false)
  const isAdmin = currentUser?.is_admin ?? false

  const load = async () => {
    try {
      setScenarios(await getScenarios())
    } catch (e) {
      setError(e instanceof Error ? e.message : t('scenario_panel.error_load_scenarios'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleCreateScenario = async (name: string, description: string) => {
    setSaving(true)
    try {
      await createScenario({ name, description })
      await load()
      setAddingScenario(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('scenario_panel.error_create'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <p className="text-gray-400 text-sm">{t('scenario_panel.loading')}</p>
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded-xl px-4 py-3 text-sm text-blue-800 dark:text-blue-300">
        {t('scenario_panel.info_text')}
        {isAdmin && <> {t('scenario_panel.info_text_admin')}</>}
      </div>

      {scenarios.length === 0 && !addingScenario && (
        <div className="text-center text-gray-400 dark:text-gray-500 py-12 text-sm">
          {isAdmin ? t('scenario_panel.no_scenarios_admin') : t('scenario_panel.no_scenarios_user')}
        </div>
      )}

      {scenarios.map((s) => (
        <ScenarioCard key={s.id} scenario={s} isAdmin={isAdmin} onRefresh={load} />
      ))}

      {addingScenario ? (
        <ScenarioForm
          onSave={handleCreateScenario}
          onCancel={() => setAddingScenario(false)}
          saving={saving}
        />
      ) : (
        isAdmin && (
          <button
            onClick={() => setAddingScenario(true)}
            className="self-start text-sm text-blue-600 hover:underline font-medium"
          >
            {t('scenario_panel.add_scenario_button')}
          </button>
        )
      )}
    </div>
  )
}
